import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Image } from 'primereact/image';
import { Button } from 'primereact/button';
import _ from 'lodash';
import FileState from '../../enums/FileState';
import { compressImages } from '../../utils/compressUtil';
import { WHEEL_PHOTO_COMPRESS_OPTIONS } from '../../constants/WheelInspectionConstants';
import { getActiveWheelPhotos } from '../../utils/wheelInspectionUtil';

const thumbSize = 96;

const addSlotClass =
  'flex align-items-center justify-content-center border-1 border-400 border-dashed border-round cursor-pointer bg-gray-50 hover:bg-gray-100';

const WheelPhotoThumbnailGrid = ({
  partCode,
  fileItems = [],
  setFileItems,
  getFileURL,
  maxFiles = 5,
  multiple = true,
  disabled = false,
  setLoading,
}) => {
  const activePhotos = getActiveWheelPhotos(fileItems, partCode);
  const remaining = Math.max(0, maxFiles - activePhotos.length);
  const canAdd = !disabled && remaining > 0;

  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (!acceptedFiles.length || remaining <= 0) return;
      if (setLoading) setLoading(true);
      try {
        const compressedImages = await compressImages(
          acceptedFiles,
          WHEEL_PHOTO_COMPRESS_OPTIONS
        );
        const slice = multiple
          ? compressedImages.slice(0, remaining)
          : compressedImages.slice(0, 1);

        slice.forEach((file) => {
          Object.assign(file, { preview: URL.createObjectURL(file) });
        });

        const newItems = slice.map((file) => ({
          partCode,
          file,
          state: FileState.Inserted,
          note: '',
        }));

        if (!multiple) {
          setFileItems((prev) => [
            ...prev.filter(
              (p) =>
                !(p.partCode === partCode && p.state !== FileState.Deleted)
            ),
            ...newItems,
          ]);
        } else {
          setFileItems((prev) => [...prev, ...newItems]);
        }
      } finally {
        if (setLoading) setLoading(false);
      }
    },
    [partCode, multiple, remaining, setFileItems, setLoading]
  );

  const { getInputProps, open } = useDropzone({
    multiple,
    noClick: true,
    noKeyboard: true,
    disabled: !canAdd,
    accept: { 'image/*': [] },
    onDrop,
  });

  const removePhoto = (targetIndex, e) => {
    e.stopPropagation();
    setFileItems((prev) => {
      const cloned = [...prev];
      const globalIdx = prev.findIndex((item, idx) => {
        if (item.partCode !== partCode || item.state === FileState.Deleted) {
          return false;
        }
        const activeBefore = prev
          .slice(0, idx + 1)
          .filter(
            (p) =>
              p.partCode === partCode && p.state !== FileState.Deleted
          ).length;
        return activeBefore - 1 === targetIndex;
      });
      if (globalIdx < 0) return prev;
      if (_.has(cloned[globalIdx], 'photoId')) {
        cloned[globalIdx] = {
          ...cloned[globalIdx],
          state: FileState.Deleted,
        };
      } else {
        cloned.splice(globalIdx, 1);
      }
      return cloned;
    });
  };

  const getThumbSrc = (item) => {
    if (_.has(item, 'photoId') && getFileURL) {
      return getFileURL(_.get(item, 'photoGroupId'), item);
    }
    return item.file?.preview;
  };

  return (
    <div className="wheel-photo-thumbnail-grid">
      <input {...getInputProps()} />
      <div className="flex flex-wrap gap-2 align-items-start">
        {activePhotos.map((item, idx) => (
          <div
            key={`${partCode}_${idx}_${item.photoId || item.file?.name}`}
            className="relative border-1 border-300 border-round overflow-hidden"
            style={{ width: thumbSize, height: thumbSize }}
          >
            <Image
              src={getThumbSrc(item)}
              alt={item.originalFileName || item.file?.name || '사진'}
              preview
              imageStyle={{
                width: thumbSize,
                height: thumbSize,
                objectFit: 'cover',
              }}
              className="w-full h-full"
            />
            {!disabled && (
              <Button
                type="button"
                icon="pi pi-times"
                rounded
                severity="danger"
                className="absolute"
                style={{ top: 4, right: 4, width: '1.5rem', height: '1.5rem' }}
                onClick={(e) => removePhoto(idx, e)}
              />
            )}
          </div>
        ))}
        {canAdd && (
          <div
            role="button"
            tabIndex={0}
            className={addSlotClass}
            style={{ width: thumbSize, height: thumbSize }}
            onClick={() => open()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                open();
              }
            }}
          >
            <div className="flex flex-column align-items-center gap-1 text-color-secondary">
              <i className="pi pi-plus" style={{ fontSize: '1.25rem' }} />
              <span className="text-xs">사진 추가</span>
            </div>
          </div>
        )}
      </div>
      {maxFiles > 1 && (
        <small className="text-color-secondary block mt-1">
          {activePhotos.length}/{maxFiles}장 (선택)
        </small>
      )}
    </div>
  );
};

export default WheelPhotoThumbnailGrid;
